import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

/**
 * Decode a JWT payload without verifying the signature.
 * Returns null if the token is malformed.
 */
function decodeTokenPayload(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

/**
 * Returns milliseconds until the token expires, or 0 if already expired.
 */
function msUntilExpiry(token) {
  const payload = decodeTokenPayload(token);
  if (!payload || !payload.exp) return 0;
  return payload.exp * 1000 - Date.now();
}

const REFRESH_BUFFER_MS = 2 * 60 * 1000; // 2 minutes before expiry

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("ic_token"));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const setAuth = useCallback((t, u) => {
    setToken(t);
    setUser(u);
    if (t) {
      localStorage.setItem("ic_token", t);
      axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    } else {
      localStorage.removeItem("ic_token");
      delete axios.defaults.headers.common["Authorization"];
      clearRefreshTimer();
    }
  }, [clearRefreshTimer]);

  /**
   * Silently refresh the access token using the httpOnly refresh cookie.
   */
  const silentRefresh = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      const newToken = res.data.token;
      const newUser = res.data.user;
      setAuth(newToken, newUser);
      scheduleRefresh(newToken);
    } catch {
      // Refresh failed — session expired, force logout
      setAuth(null, null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAuth]);

  /**
   * Schedule the next silent refresh 2 minutes before the access token expires.
   */
  const scheduleRefresh = useCallback((accessToken) => {
    clearRefreshTimer();
    if (!accessToken) return;

    const remaining = msUntilExpiry(accessToken);
    if (remaining <= 0) {
      // Already expired — refresh now
      silentRefresh();
      return;
    }

    const delay = Math.max(remaining - REFRESH_BUFFER_MS, 1000);
    refreshTimerRef.current = setTimeout(silentRefresh, delay);
  }, [clearRefreshTimer, silentRefresh]);

  // On mount: validate existing token via /me, then schedule refresh
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    axios.get(`${API}/auth/me`)
      .then(res => {
        setUser(res.data.user);
        scheduleRefresh(token);
      })
      .catch(() => {
        // Access token expired — try silent refresh before giving up
        silentRefresh().catch(() => setAuth(null, null));
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password, loginContext) => {
    const payload = { email, password };
    if (loginContext) payload.login_context = loginContext;
    const res = await axios.post(`${API}/auth/login`, payload, { withCredentials: true });
    setAuth(res.data.token, res.data.user);
    scheduleRefresh(res.data.token);
    return res.data.user;
  };

  const updateUser = (u) => setUser(u);

  const logout = async () => {
    clearRefreshTimer();
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Best-effort — proceed with client-side logout regardless
    }
    setAuth(null, null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, updateUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
